import { useEffect, useRef, useState, type ReactNode } from "react";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import { useText } from "../../clients/words";

export interface Proekt {
  /** `0` — «Весь филиал»: всё, что не отдано проектам. */
  id: number;
  slovo: string;
}

export interface Istochnik {
  kod: string;
  slovo: string;
}

/**
 * ⛔ ФИЛЬТР «ВСЕ ПРОЕКТЫ ▾» — РЯДОМ С «ВСЕ ЛИНИИ» (`s675`, слово владельца 25.09:
 * «это какой аналог разных воронок в Амо, под разные проекты внутри филиала»).
 * Список — с сервера (проекты филиала шапки и «Весь филиал»); одна или несколько;
 * считает сервер. Пока у филиала нет проектов — фильтра нет. Один компонент на
 * доску лидов, «Обращения» и «Автоматизацию».
 */
export function VyborProekta({
  proekty,
  vybrany,
  onVybor,
  className,
}: {
  proekty: Proekt[];
  vybrany: number[];
  onVybor: (ids: number[]) => void;
  className?: string;
}) {
  const text = useText();
  if (proekty.length === 0) return null;

  const vse = text("proekty.vse");
  const podpis = vybrany.length === 0
    ? vse
    : vybrany.length === 1
      ? (proekty.find((p) => p.id === vybrany[0])?.slovo ?? vse)
      : text("proekty.neskolko", { число: vybrany.length });

  const pereklyuchit = (id: number) =>
    onVybor(vybrany.includes(id) ? vybrany.filter((v) => v !== id) : [...vybrany, id].sort((a, b) => a - b));

  return (
    <Vypadashka podpis={podpis} aktiven={vybrany.length > 0} mnogo className={className} metka="data-vybor-proekta">
      {(zakryt) => (
        <>
          <Punkt slovo={vse} vybran={vybrany.length === 0} onClick={() => { onVybor([]); zakryt(); }} />
          {proekty.map((p) => (
            <Punkt key={p.id} slovo={p.slovo} vybran={vybrany.includes(p.id)} onClick={() => pereklyuchit(p.id)} />
          ))}
        </>
      )}
    </Vypadashka>
  );
}

/**
 * ⛔ ФИЛЬТР «ИСТОЧНИК» ДОСКИ ЛИДОВ (`s675`, слово владельца 25.09: «источники не должны
 * иметь разные колонки»). Колонок-источников больше нет — источник выбирается здесь;
 * правило то же, что у метки карточки и условия бота. Считает сервер.
 */
export function VyborIstochnika({
  istochniki,
  vybran,
  onVybor,
  className,
}: {
  istochniki: Istochnik[];
  vybran: string | null;
  onVybor: (kod: string | null) => void;
  className?: string;
}) {
  const text = useText();
  if (istochniki.length === 0) return null;

  const vse = text("doska.istochnik.vse");
  const podpis = vybran === null ? vse : (istochniki.find((i) => i.kod === vybran)?.slovo ?? vse);

  return (
    <Vypadashka podpis={podpis} aktiven={vybran !== null} className={className} metka="data-vybor-istochnika">
      {(zakryt) => (
        <>
          <Punkt slovo={vse} vybran={vybran === null} onClick={() => { onVybor(null); zakryt(); }} />
          {istochniki.map((i) => (
            <Punkt key={i.kod} slovo={i.slovo} vybran={vybran === i.kod} onClick={() => { onVybor(i.kod); zakryt(); }} />
          ))}
        </>
      )}
    </Vypadashka>
  );
}

function Vypadashka({
  podpis,
  aktiven,
  mnogo = false,
  className,
  metka,
  children,
}: {
  podpis: string;
  aktiven: boolean;
  mnogo?: boolean;
  className?: string;
  metka: string;
  children: (zakryt: () => void) => ReactNode;
}) {
  const [otkryt, setOtkryt] = useState(false);
  const korob = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!otkryt) return;
    const zakryt = (e: MouseEvent) => {
      if (korob.current !== null && !korob.current.contains(e.target as Node)) setOtkryt(false);
    };
    document.addEventListener("mousedown", zakryt);

    return () => document.removeEventListener("mousedown", zakryt);
  }, [otkryt]);

  return (
    <div ref={korob} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => setOtkryt((b) => !b)}
        aria-haspopup="listbox"
        aria-expanded={otkryt}
        {...{ [metka]: true }}
        className={
          "flex h-full max-w-[260px] items-center gap-1 rounded-ops-pill border px-3 py-1 text-ops-caption "
          + (aktiven
            ? "border-ops-primary bg-ops-primary text-ops-on-primary"
            : "border-ops-border text-ops-text-secondary hover:bg-ops-surface-2")
        }
      >
        <span className="truncate">{podpis}</span>
        <IconChevronDown size={14} className="shrink-0" />
      </button>

      {otkryt && (
        <div
          role="listbox"
          aria-multiselectable={mnogo}
          className="absolute left-0 top-full z-40 mt-1 min-w-[220px] overflow-hidden rounded-ops-card border border-ops-border bg-ops-surface shadow-ops-drop"
        >
          {children(() => setOtkryt(false))}
        </div>
      )}
    </div>
  );
}

function Punkt({ slovo, vybran, onClick }: { slovo: string; vybran: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={vybran}
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-ops-caption text-ops-text hover:bg-ops-surface-2"
    >
      <span className="flex-1 truncate">{slovo}</span>
      {vybran && <IconCheck size={15} className="shrink-0 text-ops-primary" />}
    </button>
  );
}
